import { useEffect } from 'react';
import { FieldValues, useForm, UseFormOptions } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

const LEAVE_CONFIRM_MESSAGE =
  'This page has unsaved changes on it, are you sure you want to leave?';

/**
 * Wrapper around useForm with blocks navigation if the form is dirty
 */
export function useLaunchpadForm<
  T extends FieldValues,
  C extends Record<string, unknown> = Record<string, unknown>
>(options?: UseFormOptions<T, C>) {
  const form = useForm<T, C>({ mode: 'onTouched', ...options });
  const history = useHistory();

  // due to react-hook-form's use of proxy, it's important to retrieve these value during render
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isDirty,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isSubmitting,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isSubmitted,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isSubmitSuccessful,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isValid,
  } = form.formState;

  // if the form is dirty, prevent users from leaving
  useEffect(() => {
    const unregiserReactRouterBlock = history.block(() => {
      if (
        form.control.formStateRef.current.isDirty &&
        !(
          form.control.formStateRef.current.isSubmitting ||
          (form.control.formStateRef.current.isSubmitted &&
            form.control.formStateRef.current.isValid) ||
          form.control.formStateRef.current.isSubmitSuccessful
        )
      ) {
        return LEAVE_CONFIRM_MESSAGE;
      }
      return undefined;
    });

    const callback = (event: BeforeUnloadEvent) => {
      if (form.control.formStateRef.current.isDirty) {
        event.preventDefault();
        // eslint-disable-next-line no-param-reassign
        event.returnValue = LEAVE_CONFIRM_MESSAGE;
      }
    };
    window.addEventListener('beforeunload', callback);

    return () => {
      if (unregiserReactRouterBlock) {
        unregiserReactRouterBlock();
      }
      window.removeEventListener('beforeunload', callback);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return form;
}
